import useTypedContext from "hooks/useTypedContext";
import { EmptystateUsersColored } from "components/icons/generated";
import EmptyState from "ds/components/EmptyState";
import LoadingIndicator from "ds/components/LoadingIndicator";
import Box from "ds/components/Box";
import MissingDataBanner from "components/MissingDataBanner";
import KeyValue from "components/KeyValue";
import SpacePath from "components/SpacePath";
import useStackRoleBindings from "shared/Stack/Roles/useStackRoleBindings";

import { StackFormContext } from "../context";
import { StackCreationWizardStep } from "../types";
import NewStackSummaryPanel from "./Panel";

type NewStackSummaryRolesProps = {
  title: string;
  goToStepDisabled?: boolean;
};

const NewStackSummaryRoles = ({ title, goToStepDisabled }: NewStackSummaryRolesProps) => {
  const { createdStackId } = useTypedContext(StackFormContext);

  const { stackRoleBindings, loading, hasData, refetch } = useStackRoleBindings({
    stackID: createdStackId,
  });

  return (
    <NewStackSummaryPanel
      name={title}
      count={stackRoleBindings.length}
      step={StackCreationWizardStep.Roles}
      goToStepDisabled={goToStepDisabled}
    >
      {!loading && hasData && stackRoleBindings.length > 0 && (
        <>
          {stackRoleBindings.map((binding) => (
            <KeyValue key={binding.id} name={binding.role.name}>
              <Box shrink="1">
                <SpacePath
                  spaceId={binding.space.id}
                  fallbackName={binding.space.name}
                  target="_blank"
                />
              </Box>
            </KeyValue>
          ))}
        </>
      )}

      {!loading && hasData && stackRoleBindings.length === 0 && (
        <EmptyState
          icon={EmptystateUsersColored}
          title="Roles not assigned"
          caption="Assign roles to let this stack perform actions outside its own space."
        />
      )}

      {loading && (
        <Box padding="large" justify="center">
          <LoadingIndicator size="x-large" loading />
        </Box>
      )}

      {!loading && !hasData && (
        <MissingDataBanner
          text="Couldn't load assigned roles, please refresh or come back later"
          refreshHandler={refetch}
          refreshLoading={loading}
        />
      )}
    </NewStackSummaryPanel>
  );
};

export default NewStackSummaryRoles;
